import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowsToCircle,
  faArrowUp,
  faBarcode,
  faBox,
  faCompress,
  faCube,
  faDrawPolygon,
  faEdit,
  faExpand,
  faGrip,
  faHand,
  faMousePointer,
  faPrint,
  faTags,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, ConfigProvider, Flex, Row, Tooltip } from "antd";
import { debounce, round } from "lodash";
import { Check, X } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import ToolTip from "../../shared/components/ToolTip/ToolTip";
import { Store } from "../../store";
import { pixelsToUnits, sortBinsByPosition, unitsToPixels } from "../../utils/helpers";
import { createElement, drawInfiniteGrid, easeToValuesRAF, isElementInSelectionArea, throttleRAF } from "../LayoutEditor/drawUtils";
import {
  adjustElementCoordinates,
  adjustmentRequired,
  cursorForPosition,
  getElementAtPosition,
  getMouseCoordinates,
  isColliding,
  resizedCoordinates,
} from "../LayoutEditor/geometryutils";
import { useCanvasDimensions, usePressedKeys, useZoomControl } from "../LayoutEditor/hooks";
import { findNearestSnapPoint, getSnapGuides } from "../LayoutEditor/snappingUtils";
import "./index.css";

import { Dropdown, Form, Input, Menu, Modal, Select } from "antd";
import BarCode from "../BarCode/BarCode";
import QrCode from "../QrCode/QrCode";

import { InfoCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import { toast } from "react-toastify";
import axios from "../../service/axios";
import { updateLayoutObject } from "../../services/layoutObjectsService";
import { MAX_CAPACITY_OPTIONS } from "../../utils/constants";
import BarCodesPreview from "../BarCodesPreview/BarCodesPreview";
import GenerateBinLabelsForm from "../GenerateBinLabelsForm/GenerateBinLabelsForm";
import GenerateBinsModal from "../GenerateBinsModal/GenerateBinsModal";
import MultiBinEdit from "./MultiBinEdit";

const { Option } = Select;

const maxCapacityOptions = MAX_CAPACITY_OPTIONS;

const GroupControls = ({ group, bounds, zoom, panOffset, onFinalize, onCancel }) => {
  const controlStyles = {
    position: "absolute",
    display: "flex",
    gap: "8px",
    top: `${bounds.y1 * zoom + panOffset.y}px`,
    left: `${bounds.x2 * zoom + panOffset.x}px`,
    transform: "translate(8px, -50%)",
    zIndex: 1000,
  };

  const buttonStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  return (
    <div style={controlStyles}>
      <button
        onClick={() => onFinalize(group)}
        style={{
          ...buttonStyles,
          backgroundColor: "rgba(34, 197, 94, 0.9)", // green
        }}
        className="hover:scale-110"
        title="Finalize group"
        type="button"
      >
        <Check size={16} color="white" />
      </button>
      <button
        type="button"
        onClick={() => onCancel(group)}
        style={{
          ...buttonStyles,
          backgroundColor: "rgba(239, 68, 68, 0.9)", // red
        }}
        className="hover:scale-110"
        title="Cancel"
      >
        <X size={16} color="white" />
      </button>
    </div>
  );
};

const BinLabelsForm = ({ onSubmit, setLabelModal }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(true);

  const onOk = async () => {
    const formValues = await form.validateFields();
    onSubmit(formValues);
    setOpen(false);
  };

  const onClose = () => {
    form.resetFields();
    setOpen(false);
    setLabelModal(false);
  };

  return (
    <Modal centered maskClosable={false} width={350} cancelText={"Cancel"} okText={"Save"} title={"Add Label"} open={open} onOk={onOk} onCancel={onClose}>
      <Form
        form={form}
        name="binLabelForm"
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        style={{
          maxWidth: 600,
        }}
        initialValues={{
          remember: true,
        }}
        // onFinish={onFinish}
        // onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Form.Item name="labelType" label="Label Type">
          <Select placeholder="Select Label" allowClear>
            <Option value="level">Level</Option>
            <Option value="column">Column</Option>
            <Option value="bay">Bay</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Label" name="labelName">
          <Input placeholder="Enter Label" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const BinEditForm = ({ onSubmit, setBinModalVisible, selectedElementId, elements, formData, loading }) => {
  const [form] = Form.useForm();
  const [isCollision, setIsCollision] = useState(false);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const selectedElement = elements.find((e) => e.id === selectedElementId); // find the selected element from the elements array
  const elementWithConvertedUnits = {
    ...selectedElement,
    width: round(pixelsToUnits(selectedElement.width, formData.unitType), 2),
    height: round(pixelsToUnits(selectedElement.height, formData.unitType), 2),
  };

  const formInitialValues = {
    binCode: elementWithConvertedUnits?.code,
    binWidth: elementWithConvertedUnits.width,
    binHeight: elementWithConvertedUnits.height,
    maxCapacity: elementWithConvertedUnits.maxCapacity || null, // default to null if not set
  };

  const onChange = () => {
    const currentFormValues = form.getFieldsValue();
    //binWidth and binHeight type to parseFloat
    currentFormValues.binCode = currentFormValues.binCode?.trim();
    currentFormValues.binWidth = parseFloat(currentFormValues.binWidth);
    currentFormValues.binHeight = parseFloat(currentFormValues.binHeight);
    currentFormValues.maxCapacity = parseInt(currentFormValues.maxCapacity, 10) || null; // parse to integer or set to null

    const hasChanges = Object.keys(formInitialValues).some((key) => {
      // if key is maxCapacity, then even if the value is changed to null, consider it as a valid (true)
      if (key === "maxCapacity") {
        return formInitialValues[key] !== currentFormValues[key];
      }

      const isEmptyValue = Boolean(currentFormValues[key]); // Check if the value is empty
      return formInitialValues[key] !== currentFormValues[key] && isEmptyValue;
    });

    //if input value changed to its initial value, after collision triggered, set isCollision to false
    if (!hasChanges && isCollision) {
      setIsCollision(false);
    }
    setIsSaveBtnDisabled(!hasChanges);
  };

  const onClose = () => {
    form.resetFields();
    setBinModalVisible(false);
  };

  const onFormSubmit = (values) => {
    const formValues = values;
    const binCode = formValues.binCode;
    let binWidth = parseFloat(formValues.binWidth);
    let binHeight = parseFloat(formValues.binHeight);
    binWidth = round(unitsToPixels(binWidth, formData.unitType), 2); //convert to pixels
    binHeight = round(unitsToPixels(binHeight, formData.unitType), 2); //convert to pixels
    const maxCapacity = formValues.maxCapacity ? Number(formValues.maxCapacity) : null; // default to null if not set

    const x2 = elementWithConvertedUnits.x1 + binWidth;
    const y1 = elementWithConvertedUnits.y2 - binHeight;

    const updatedElement = { ...elementWithConvertedUnits, code: binCode, width: binWidth, height: binHeight, x2, y1, maxCapacity };
    //check for collisioin
    const isCollisionTriggered = elements.some((element) => element.id !== selectedElementId && isColliding(element, updatedElement));
    setIsCollision(isCollisionTriggered);
    if (!isCollisionTriggered) {
      onSubmit(updatedElement);
    }
  };

  return (
    <Modal
      centered
      maskClosable={false}
      width={350}
      cancelText={"Cancel"}
      okText={"Update"}
      title={"Edit Bin"}
      open={true}
      onOk={() => form.submit()}
      onCancel={onClose}
      okButtonProps={{ disabled: isSaveBtnDisabled, loading: loading }}
    >
      <Form
        form={form}
        name="binInfoForm"
        onFinish={onFormSubmit}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 16,
        }}
        style={{
          maxWidth: 600,
        }}
        initialValues={formInitialValues}
        autoComplete="off"
      >
        <Form.Item label="Bin Code" name="binCode">
          <Input type="text" name="binCode" placeholder="Enter Code" onChange={onChange} />
        </Form.Item>
        <Form.Item label="Bin Width" name="binWidth">
          <Input type="number" className="no-spin-input" name="binWidth" placeholder="Enter Width" onChange={onChange} />
        </Form.Item>
        <Form.Item label="Bin Height" name="binHeight">
          <Input type="number" className="no-spin-input" name="binHeight" placeholder="Enter Height" onChange={onChange} />
        </Form.Item>
        {/* Max capacity selection */}
        <Form.Item
          label={<span>Max Capacity</span>}
          name="maxCapacity"
          rules={[
            {
              pattern: /^\d{0,4}$/,
              message: "Max 4 digits allowed",
            },
          ]}
        >
          <Input
            placeholder="Enter Capacity"
            maxLength={4}
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                onChange();
              }
            }}
          />
        </Form.Item>
      </Form>
      {isCollision && !isSaveBtnDisabled && (
        <div style={{ textAlign: "center", color: "red" }}>
          <InfoCircleOutlined rotate={180} style={{ marginRight: 5, marginBottom: 0 }} />
          BINS OVERLAPPING{" "}
        </div>
      )}
    </Modal>
  );
};

const BinDeleteConfirmModal = ({ selectedElementId, elements, setDeleteModal }) => {
  const selectedElement = elements.find((e) => e.id === selectedElementId);

  const onClose = () => {
    setDeleteModal(false);
  };
  return (
    <Modal
      centered
      maskClosable={false}
      width={350}
      cancelText={"Cancel"}
      okText={"Delete"}
      title={"Edit Code"}
      open={true}
      // onOk={onOk}
      onCancel={onClose}
    >
      <h1>Confirm Delete: {selectedElement.code}</h1>
    </Modal>
  );
};

const ExportBarCodesModal = ({ elements, formData, setToggleExportBarCodes, labelType }) => {
  const sortedStorageLocations = sortBinsByPosition(elements);

  return (
    <Modal
      style={{ margin: "25px 0px" }}
      centered
      maskClosable={false}
      width={800}
      cancelText={"Cancel"}
      okText={"Save"}
      title={"Preview"}
      open={true}
      onCancel={() => setToggleExportBarCodes(false)}
      footer={null}
    >
      <BarCodesPreview barcodes={sortedStorageLocations} name={formData.rackName} labelType={labelType} />
    </Modal>
  );
};

const RackFrontView = ({ formData }) => {
  // Refs, keep all the userRefs here
  const canvasRef = useRef(null);

  const containerRef = useRef(null);
  const canvasParentRef = useRef(null);
  // States, keep all the userStates here
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [selectionArea, setSelectionArea] = useState(null);
  const [selectedElements, setSelectedElements] = useState(new Set());
  const [snapGuides, setSnapGuides] = useState([]);
  const [labelMode, setLabelMode] = useState(null); // 'level', 'column', 'bay'
  const [labelGroups, setLabelGroups] = useState([]);
  const [labelName, setLabelName] = useState("");
  const [animations, setAnimations] = useState([]);

  const [labelModal, setLabelModal] = useState(false);
  const [binModal, setBinModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showGenerateBinLabelsForm, setShowGenerateBinLabelsForm] = useState(false);
  const [barCodeModal, setBarCodeModal] = useState(false);
  const [toggleGenerateBins, setToggleGenerateBins] = useState(false);

  const [binEditLoading, setBinEditLoading] = useState(false);

  const [toggleExportBarCodes, setToggleExportBarCodes] = useState(false);

  // Add new state for pending groups
  const [pendingGroups, setPendingGroups] = useState([]);
  // Track which group is being moved
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);

  // Add clipboard state
  const [clipboardItems, setClipboardItems] = useState([]);

  //Scannable Label
  const [scannableLabelType, setScannableLabelType] = useState("BARCODE");
  const [showMultiBinEditModal, setShowMultiBinEditModal] = useState(false);

  // redux
  const { state, dispatch } = useContext(Store);
  const selectedRackInfo = state.layoutEditor.selectedElement;
  const { tool, action, canvasDimensions, selectedElement, panOffset, zoom, zoomCenter, startPanMousePosition, elements } = state.rackEditor;

  const pressedKeys = usePressedKeys();
  useZoomControl(zoom, zoomCenter);
  useCanvasDimensions(canvasParentRef, canvasRef, (dimensions) => {
    dispatch({ type: "RE_SET_CANVAS_DIMENSIONS", payload: dimensions });
  });

  useEffect(() => {
    resetView();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete") {
        if (selectedElements.size > 0) {
          const updatedElements = elements.filter((element) => !selectedElements.has(element.id));
          dispatch({ type: "RE_SET_ELEMENTS", payload: updatedElements });
          setSelectedElements(new Set()); // Clear selection after delete

          // If we had a single selected element in the redux store, clear it
          if (selectedElement) {
            dispatch({ type: "RE_SET_SELECTED_ELEMENT", payload: null });
          }
        }
      }
    };

    canvasRef.current.addEventListener("keydown", handleKeyDown);
    return () => {
      canvasRef.current?.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedElements, elements, dispatch, selectedElement]);

  // Add keyboard event handlers for copy/paste
  useEffect(() => {
    const handleKeyboardShortcuts = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "c") {
        handleCopy();
      } else if ((event.ctrlKey || event.metaKey) && event.key === "v") {
        handlePaste();
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcuts);
    return () => window.removeEventListener("keydown", handleKeyboardShortcuts);
  }, [selectedElements, elements]);

  const handleCopy = () => {
    if (selectedElements.size === 0) return;

    // Get selected elements
    const selectedBins = elements.filter((el) => selectedElements.has(el.id));

    // Calculate group bounds
    const minX = Math.min(...selectedBins.map((el) => el.x1));
    const minY = Math.min(...selectedBins.map((el) => el.y1));

    // Store elements with relative positions
    const clipboardData = selectedBins.map((element) => ({
      ...element,
      relativeX: element.x1 - minX,
      relativeY: element.y1 - minY,
      width: element.x2 - element.x1,
      height: element.y2 - element.y1,
    }));

    setClipboardItems(clipboardData);
  };

  const handlePaste = () => {
    if (clipboardItems.length === 0) return;

    // Calculate paste position (offset from original)
    const offset = 20; // 20 units offset
    const pasteOffset = pendingGroups.length * offset;

    // Create new group with unique IDs
    const newGroup = {
      id: Date.now(),
      elements: clipboardItems.map((item) => ({
        ...item,
        id: Math.random().toString(36).substr(2, 9),
        x1: item.x1 + pasteOffset,
        y1: item.y1 + pasteOffset,
        x2: item.x2 + pasteOffset,
        y2: item.y2 + pasteOffset,
        code: `${item.code}_copy`,
      })),
      offset: { x: 0, y: 0 }, // For group movement
    };

    setPendingGroups([...pendingGroups, newGroup]);
  };

  const moveGroup = (groupIndex, dx, dy) => {
    setPendingGroups((prevGroups) =>
      prevGroups.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              offset: {
                x: (group.offset?.x || 0) + dx,
                y: (group.offset?.y || 0) + dy,
              },
            }
          : group,
      ),
    );
  };

  // Function to get relative bounds
  const getRelativeBounds = (bounds) => {
    if (!containerRef.current) return bounds;
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      x1: bounds.x1,
      y1: bounds.y1,
      x2: bounds.x2,
      y2: bounds.y2,
    };
  };

  const finalizeGroup = (groupIndex) => {
    const group = pendingGroups[groupIndex];
    const groupElements = group.elements.map((element) => ({
      ...element,
      x1: element.x1 + group.offset.x,
      y1: element.y1 + group.offset.y,
      x2: element.x2 + group.offset.x,
      y2: element.y2 + group.offset.y,
    }));

    // Check for collisions
    const hasCollisions = groupElements.some((newElement) => elements.some((existingElement) => isColliding(newElement, existingElement)));

    if (hasCollisions) {
      // Show collision warning
      toast.error("bins are overlapping with other bins");
      return;
    }

    // Add elements to main elements array
    dispatch({
      type: "RE_SET_ELEMENTS",
      payload: [...elements, ...groupElements],
    });

    // Remove group from pending
    setPendingGroups((prevGroups) => prevGroups.filter((_, index) => index !== groupIndex));
  };

  const cancelGroup = (groupIndex) => {
    setPendingGroups((prevGroups) => prevGroups.filter((_, index) => index !== groupIndex));
  };

  /**use refs to hold pan offset and zoom */
  const panOffsetRef = useRef(panOffset);
  const zoomRef = useRef(zoom);
  panOffsetRef.current = panOffset;
  zoomRef.current = zoom;

  // handle pan and zoom by track-pad swipe
  const handlePanAndZoom = debounce((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Zoom in and out using track pad
    if (e.ctrlKey) {
      const zoomSpeed = 0.02;
      const delta = -e.deltaY * zoomSpeed;
      const newZoom = zoomRef.current * (1 + delta);
      if (newZoom > 0.01 && newZoom < 5) {
        const mouseX = (e.clientX - panOffsetRef.current.x) / zoomRef.current;
        const mouseY = (e.clientY - panOffsetRef.current.y) / zoomRef.current;
        const panXOffset = e.clientX - mouseX * newZoom;
        const panYOffset = e.clientY - mouseY * newZoom;
        dispatch({
          type: "BATCH_DISPATCH",
          payload: [
            { type: "RE_SET_ZOOM", payload: newZoom },
            { type: "RE_SET_PAN_OFFSET", payload: { x: panXOffset, y: panYOffset } },
          ],
        });
        return;
      }
    }
    // Pan the canvas using track pad swipe
    const dx = -e.deltaX * 1.2;
    const dy = -e.deltaY * 1.2;
    dispatch({
      type: "RE_SET_PAN_OFFSET",
      payload: { x: panOffsetRef.current.x + dx, y: panOffsetRef.current.y + dy },
    });
  }, 9);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("wheel", handlePanAndZoom, { passive: false });
      return () => {
        canvas.removeEventListener("wheel", handlePanAndZoom);
      };
    }
  }, []);

  // // Add a delete button to the toolbar
  const handleDelete = () => {
    setTool("Delete");
    setDeleteModal(true);
    if (selectedElements.size > 0) {
      const updatedElements = elements.filter((element) => !selectedElements.has(element.id));
      dispatch({ type: "RE_SET_ELEMENTS", payload: updatedElements });
      setSelectedElements(new Set());

      if (selectedElement) {
        dispatch({ type: "RE_SET_SELECTED_ELEMENT", payload: null });
      }
    }
  };

  const handleLabels = () => {
    setLabelModal(true);
    setTool("Label");
  };

  const handleBinInfo = () => {
    setBinModal(true);
    setTool("BinInfo");
  };

  const handleMouseDown = (event) => {
    const { clientX, clientY } = getMouseCoordinates({
      event,
      zoom,
      panOffset,
    });

    // Check if clicking on a pending group
    const groupIndex = pendingGroups.findIndex((group) => {
      const bounds = getGroupBounds(group);
      return isPointInRect(clientX, clientY, bounds);
    });

    if (groupIndex !== -1) {
      setActiveGroupIndex(groupIndex);
      dispatch({ type: "RE_SET_ACTION", payload: "moving-group" });
      return;
    }

    // Check if clicking within selection bounds
    if (tool === "selection" && isPointInSelectionBounds(clientX, clientY)) {
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "RE_SET_ACTION", payload: "moving" },
          {
            type: "RE_SET_SELECTED_ELEMENT",
            payload: {
              offsetX: clientX - elements.find((e) => e.id === Array.from(selectedElements)[0]).x1,
              offsetY: clientY - elements.find((e) => e.id === Array.from(selectedElements)[0]).y1,
            },
          },
        ],
      });
      return;
    }

    if (tool === "pan" || event.button === 1 || pressedKeys.has(" ")) {
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "RE_SET_ACTION", payload: "panning" },
          {
            type: "RE_SET_START_PAN_MOUSE_POSITION",
            payload: { x: clientX, y: clientY },
          },
        ],
      });
      return;
    }
    if (tool === "draw") {
      const element = createElement({
        type: "rectangle",
        objectType: "bin",
        rackCode: selectedRackInfo.code,
        zIndex: elements.length,
        x1: clientX,
        y1: clientY,
        x2: clientX,
        y2: clientY,
        color: "black", //FIXME24
        maxCapacity: formData.maxCapacity || null,
      });

      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "RE_SET_SELECTED_ELEMENT", payload: element },
          { type: "RE_SET_ELEMENTS", payload: [...elements, element] },
          { type: "RE_SET_ACTION", payload: "drawing" },
        ],
      });
    }
    if (tool === "selection") {
      const element = getElementAtPosition(clientX, clientY, elements);
      if (element) {
        const offsetX = clientX - element.x1;
        const offsetY = clientY - element.y1;

        if (event.shiftKey) {
          const newSelection = new Set(selectedElements);
          if (newSelection.has(element.id)) {
            newSelection.delete(element.id);
          } else {
            newSelection.add(element.id);
          }

          setSelectedElements(newSelection);
        } else if (!selectedElements.has(element.id)) {
          setSelectedElements(new Set([element.id]));
        }

        dispatch({
          type: "RE_SET_SELECTED_ELEMENT",
          payload: { ...element, offsetX, offsetY },
        });
        if (element.position === "inside") {
          dispatch({ type: "RE_SET_ACTION", payload: "moving" });
        } else {
          dispatch({ type: "RE_SET_ACTION", payload: "resizing" });
        }
      } else {
        setSelectionArea({
          x1: clientX,
          y1: clientY,
          x2: clientX,
          y2: clientY,
        });
        if (!event.shiftKey) {
          setSelectedElements(new Set());
        }
        dispatch({ type: "RE_SET_ACTION", payload: "selecting" });
      }
    }
  };
  const handleMouseMove = (event) => {
    if (action === "moving-group" && activeGroupIndex !== null) {
      const { movementX, movementY } = event;
      const dx = movementX / zoom;
      const dy = movementY / zoom;
      moveGroup(activeGroupIndex, dx, dy);
      return;
    }

    const { clientX, clientY } = getMouseCoordinates({
      event,
      zoom,
      panOffset,
    });
    // setMousePosition({ x: clientX, y: clientY });

    if (action === "selecting" && selectionArea) {
      // Update selection area
      setSelectionArea((prev) => ({
        ...prev,
        x2: clientX,
        y2: clientY,
      }));

      // Find elements within selection area
      const selectedIds = new Set();
      elements.forEach((element) => {
        if (
          isElementInSelectionArea(
            element,
            {
              x1: selectionArea.x1,
              y1: selectionArea.y1,
              x2: clientX,
              y2: clientY,
            },
            zoom,
            panOffset,
          )
        ) {
          selectedIds.add(element.id);
        }
      });
      setSelectedElements(selectedIds);
    }

    if (action === "panning") {
      const deltaX = (clientX - startPanMousePosition.x) * zoom * 0.5;
      const deltaY = (clientY - startPanMousePosition.y) * zoom * 0.5;
      dispatch({
        type: "RE_SET_PAN_OFFSET",
        payload: { x: panOffset.x + deltaX, y: panOffset.y + deltaY },
      });
      return;
    }

    if (tool === "selection") {
      const element = getElementAtPosition(clientX, clientY, elements);
      event.target.style.cursor = element ? cursorForPosition(element.position) : "default";
    }

    if (action === "drawing") {
      const index = elements.length - 1;
      const { x1, y1, type } = elements[index];

      const snappedPosition = findNearestSnapPoint({ x: clientX, y: clientY }, elements, zoom, panOffset, index);

      // Update guides for visualization
      const guides = getSnapGuides({ x: clientX, y: clientY }, elements, zoom, panOffset, index);
      setSnapGuides(guides);

      const updatedElement = {
        ...elements[index],
        x2: snappedPosition.x || clientX,
        y2: snappedPosition.y || clientY,
      };

      for (const elementToCheck of elements) {
        if (elementToCheck.id === updatedElement.id) continue;
        const isCollidingWithElement = isColliding(updatedElement, elementToCheck);
        if (isCollidingWithElement) {
          console.error("Element collides with another element", elementToCheck, updatedElement);
          return;
        }
      }

      const elementsCopy = [...elements];
      elementsCopy[index] = updatedElement;
      dispatch({ type: "RE_SET_ELEMENTS", payload: elementsCopy });
    } else if (action === "moving") {
      const { id, x1, x2, y1, y2, type, offsetX, offsetY, zIndex } = selectedElement;
      //const width = x2 - x1;
      //const height = y2 - y1;
      // Find snap points for the top-left corner
      const snappedPosition = findNearestSnapPoint({ x: clientX - offsetX, y: clientY - offsetY }, elements, zoom, panOffset, id);

      // Update guides for visualization
      const guides = getSnapGuides({ x: clientX - offsetX, y: clientY - offsetY }, elements, zoom, panOffset, id);
      setSnapGuides(guides);

      const newX1 = clientX - offsetX;
      const newY1 = clientY - offsetY;
      // let elementToUpdate = {
      //     ...selectedElement,
      //     id,
      //     x1: snappedPosition.x || newX1,
      //     y1: snappedPosition.y || newY1,
      //     x2: (snappedPosition.x || newX1)+ width,
      //     y2: (snappedPosition.y || newY1) + height,
      //     type,
      //     zIndex,
      // };
      // Calculate the movement delta from the initial position for the primary selected element
      const initialElement = elements.find((el) => el.id === id);
      const deltaX = newX1 - initialElement.x1;
      const deltaY = newY1 - initialElement.y1;

      // Create updated versions of all elements
      const updatedElements = elements.map((element) => {
        if (selectedElements.has(element.id)) {
          const elementWidth = element.x2 - element.x1;
          const elementHeight = element.y2 - element.y1;
          const newElementX1 = element.x1 + deltaX;
          const newElementY1 = element.y1 + deltaY;

          return {
            ...element,
            x1: newElementX1,
            y1: newElementY1,
            x2: newElementX1 + elementWidth,
            y2: newElementY1 + elementHeight,
          };
        }
        return element;
      });
      // const colliding = elements.some((element) => element.id !== elementToUpdate.id && isColliding(element, elementToUpdate));
      // Check for collisions with non-selected elements
      const hasCollisions = updatedElements
        .filter((el) => selectedElements.has(el.id))
        .some((updatedElement) => {
          return elements.filter((el) => !selectedElements.has(el.id)).some((element) => isColliding(updatedElement, element));
        });

      if (hasCollisions) {
        console.error("Element collides with another element");
        return;
      }

      dispatch({ type: "RE_SET_ELEMENTS", payload: updatedElements });
    } else if (action === "resizing") {
      const { id, type, position, zIndex, ...coordinates } = selectedElement;

      // Find snap points for the moving corner
      const snappedPosition = findNearestSnapPoint({ x: clientX, y: clientY }, elements, zoom, panOffset, id);

      // Update guides for visualization
      const guides = getSnapGuides({ x: clientX, y: clientY }, elements, zoom, panOffset, id);
      setSnapGuides(guides);

      const { x1, y1, x2, y2 } = resizedCoordinates(snappedPosition.x, snappedPosition.y, position, coordinates);

      const elementToUpdate = {
        ...selectedElement,
        id,
        x1,
        y1,
        x2,
        y2,
        type,
        zIndex,
      };

      const colliding = elements.some((element) => element.id !== elementToUpdate.id && isColliding(element, elementToUpdate));
      if (colliding) {
        console.error("Element collides with another element");
        return;
      }
      dispatch({ type: "RE_UPDATE_ELEMENT", payload: elementToUpdate });
    } else {
      // setSnapGuides([]);
    }
  };

  const getGroupBounds = (group) => {
    const elements = group.elements.map((el) => ({
      ...el,
      x1: el.x1 + (group.offset?.x || 0),
      y1: el.y1 + (group.offset?.y || 0),
      x2: el.x2 + (group.offset?.x || 0),
      y2: el.y2 + (group.offset?.y || 0),
    }));

    return {
      x1: Math.min(...elements.map((el) => el.x1)),
      y1: Math.min(...elements.map((el) => el.y1)),
      x2: Math.max(...elements.map((el) => el.x2)),
      y2: Math.max(...elements.map((el) => el.y2)),
    };
  };

  // Helper function to check if point is in rectangle
  const isPointInRect = (x, y, rect) => {
    return x >= rect.x1 && x <= rect.x2 && y >= rect.y1 && y <= rect.y2;
  };

  const isPointInSelectionBounds = (x, y) => {
    if (selectedElements.size === 0) return false;

    // Calculate bounds of all selected elements
    const selectedBounds = Array.from(selectedElements).reduce(
      (bounds, elementId) => {
        const element = elements.find((e) => e.id === elementId);
        if (!element) return bounds;

        const x1 = element.x1 * zoom + panOffset.x;
        const y1 = element.y1 * zoom + panOffset.y;
        const x2 = element.x2 * zoom + panOffset.x;
        const y2 = element.y2 * zoom + panOffset.y;

        return {
          minX: Math.min(bounds.minX, x1),
          minY: Math.min(bounds.minY, y1),
          maxX: Math.max(bounds.maxX, x2),
          maxY: Math.max(bounds.maxY, y2),
        };
      },
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
    );

    // Add padding to the bounds (10 pixels on each side)
    return x >= selectedBounds.minX - 10 && x <= selectedBounds.maxX + 10 && y >= selectedBounds.minY - 10 && y <= selectedBounds.maxY + 10;
  };

  const handleMouseUp = (event) => {
    const actionsToDispatch = [];

    if (action === "moving-group") {
      setActiveGroupIndex(null);
    }

    if (action === "selecting") {
      setSelectionArea(null);
    }

    if (tool === "pan") {
      actionsToDispatch.push({ type: "RE_SET_TOOL", payload: "pan" });
    } else {
      actionsToDispatch.push({ type: "RE_SET_TOOL", payload: "selection" });
    }
    actionsToDispatch.push({ type: "RE_SET_ACTION", payload: "none" });

    if (selectedElement) {
      const index = elements.findIndex((obj) => obj.id === selectedElement.id);
      if (index != -1 && (action === "drawing" || action === "resizing") && adjustmentRequired(elements[index].type)) {
        const { x1, y1, x2, y2 } = adjustElementCoordinates(elements[index]);

        const width = round(x2 - x1, 2);
        const height = round(y2 - y1, 2);

        if (width === 0 || height === 0) {
          actionsToDispatch.push({
            type: "RE_REMOVE_ELEMENT",
            payload: elements[index],
          });
        } else {
          const elementToUpdate = {
            ...elements[index],
            x1,
            y1,
            x2,
            y2,
            positionX: round(x1, 2),
            positionY: round(y1, 2),
            width: round(x2 - x1, 2),
            height: round(y2 - y1, 2),
            type: elements[index].type,
          };
          actionsToDispatch.push({
            type: "RE_UPDATE_ELEMENT",
            payload: elementToUpdate,
          });
        }
      }
      actionsToDispatch.push({
        type: "RE_SET_SELECTED_ELEMENT",
        payload: elements[index],
      });
    }

    dispatch({
      type: "BATCH_DISPATCH",
      payload: actionsToDispatch,
    });
  };

  useEffect(() => {
    // change the cursor to hand when move tool selected
    if (tool === "pan") {
      canvasRef.current.style.cursor = "grab";
      if (action === "panning") {
        canvasRef.current.style.cursor = "grabbing";
      }
    } else {
      canvasRef.current.style.cursor = "default";
    }
  }, [tool, action]);

  const applyLabel = (label) => {
    const actionsToDispatch = [];

    selectedElements.forEach((elementId) => {
      const element = elements.find((e) => e.id === elementId);
      const existingLabels = element.labels || [];
      const existingLabelWithType = existingLabels.find((l) => l.type === labelMode);
      if (existingLabelWithType) {
        existingLabelWithType.name = label;
      } else {
        existingLabels.push({ type: labelMode, name: label });
      }
      actionsToDispatch.push({
        type: "RE_UPDATE_ELEMENT",
        payload: { ...element, labels: existingLabels },
      });
    });
    dispatch({
      type: "BATCH_DISPATCH",
      payload: actionsToDispatch,
    });
    setLabelMode(null);
    // setLabelName("");
  };

  const onModalHandler = () => {
    setDialogData({
      title: "Set Labels",
      isModalActive: true,
      dialogWidth: 400,
      showCancelLabel: true,
      showConfirmLabel: true,
      cancelLabel: "Cancel",
      confirmLabel: "Apply Label",
    });
  };

  const renderScene = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!canvas) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawInfiniteGrid({ canvas, ctx, panOffset, zoom, showXYAxis: false });

    const width = selectedRackInfo.x2 - selectedRackInfo.x1;
    const len = selectedRackInfo.y2 - selectedRackInfo.y1;

    const RACK_WIDTH = Math.max(width, len); // example: 600mm width
    const BASELINE_Y = 0;

    // Draw rack boundaries
    ctx.save();

    // Calculate rack boundaries in screen coordinates
    const rackLeft = 0 * zoom + panOffset.x;
    const rackRight = RACK_WIDTH * zoom + panOffset.x;
    const baselineY = BASELINE_Y * zoom + panOffset.y;

    // Draw vertical boundaries
    ctx.beginPath();
    ctx.strokeStyle = "#2196F3"; // Material Blue
    ctx.lineWidth = 2;
    ctx.setLineDash([8, 4]); // Dashed lines for vertical boundaries

    // Left boundary
    ctx.moveTo(rackLeft, 0);
    ctx.lineTo(rackLeft, canvas.height);

    // Right boundary
    ctx.moveTo(rackRight, 0);
    ctx.lineTo(rackRight, canvas.height);
    ctx.stroke();

    // Draw baseline
    ctx.beginPath();
    ctx.strokeStyle = "#FF4444"; // Red for baseline
    ctx.lineWidth = 2;
    ctx.moveTo(rackLeft, baselineY);
    ctx.lineTo(rackRight, baselineY);
    ctx.stroke();

    // Add labels
    ctx.fillStyle = "#2196F3";
    ctx.font = "14px Arial";

    // Width label near baseline
    const widthLabel = `${round(pixelsToUnits(RACK_WIDTH, formData.unitType), 2)} ${formData?.unitType}`;
    const widthLabelWidth = ctx.measureText(widthLabel).width;
    ctx.fillText(widthLabel, rackLeft + (RACK_WIDTH * zoom - widthLabelWidth) / 2, baselineY + 20);

    // Baseline label
    // ctx.fillStyle = "#FF4444";
    // ctx.fillText("RACK BASE LINE", rackLeft + 10, baselineY + 20);

    ctx.restore();

    elements?.forEach((element) => {
      let { id, x1, y1, x2, y2, name, color } = element;

      x1 = x1 * zoom + panOffset.x;
      y1 = y1 * zoom + panOffset.y;
      x2 = x2 * zoom + panOffset.x;
      y2 = y2 * zoom + panOffset.y;

      ctx.save();
      // if (selectedElements.has(id)) {
      //     ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
      //     ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
      // } else {
      //     ctx.strokeStyle = "rgba(0, 0, 255, 0.6)";
      //     ctx.fillStyle = "rgba(0, 0, 255, 0.1)";
      // }

      ctx.strokeStyle = "rgba(0, 0, 255, 0.6)";
      ctx.fillStyle = "rgba(0, 0, 255, 0.1)";
      ctx.lineWidth = 2;
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.restore();

      //TESTING CODE: VISUAL INDICATOR FOR SELECTED ELEMENTS
      // Draw selection rectangle if there are selected elements
      if (selectedElements.size > 0) {
        // Calculate bounds of all selected elements
        const selectedBounds = Array.from(selectedElements).reduce(
          (bounds, elementId) => {
            const element = elements.find((e) => e.id === elementId);
            if (!element) return bounds;

            const x1 = element.x1 * zoom + panOffset.x;
            const y1 = element.y1 * zoom + panOffset.y;
            const x2 = element.x2 * zoom + panOffset.x;
            const y2 = element.y2 * zoom + panOffset.y;

            return {
              minX: Math.min(bounds.minX, x1),
              minY: Math.min(bounds.minY, y1),
              maxX: Math.max(bounds.maxX, x2),
              maxY: Math.max(bounds.maxY, y2),
            };
          },
          { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
        );

        // Draw dotted selection rectangle
        ctx.save();
        ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
        ctx.lineWidth = 1;
        //ctx.setLineDash([2, 2]);
        ctx.strokeRect(
          selectedBounds.minX - 5,
          selectedBounds.minY - 5,
          selectedBounds.maxX - selectedBounds.minX + 10,
          selectedBounds.maxY - selectedBounds.minY + 10,
        );
        ctx.restore();
      }

      // render name of element at the center, proportionally to the size of the element

      // calculate font size based on element size
      const elementWidth = x2 - x1;
      const fontSize = Math.min(20, Math.floor(elementWidth / 10));

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `${fontSize}px sans-serif`;
      ctx.fillStyle = "black";
      const elementCenterX = (x1 + x2) / 2;
      const elementCenterY = (y1 + y2) / 2;
      ctx.fillText(element.code, elementCenterX, elementCenterY);

      const dimensionsInfo = `${round(pixelsToUnits(element.x2 - element.x1, formData.unitType), 2)} x ${round(
        pixelsToUnits(element.y2 - element.y1, formData.unitType),
        2,
      )} ${formData?.unitType}`;
      ctx.fillText(dimensionsInfo, elementCenterX, elementCenterY + fontSize * 1.1);

      ctx.restore();

      // draw labels on the element
      if (element.labels) {
        element.labels.forEach((label, index) => {
          ctx.save();
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `${fontSize}px sans-serif`;
          ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
          const labelX = (x1 + x2) / 2;
          const labelY = (y1 + y2) / 2 + index * fontSize + fontSize * 1.1;
          ctx.fillText(`${label.type}:${label.name}`, labelX, labelY + fontSize * 1.1);
          ctx.restore();
        });
      }
    });
    if (selectionArea) {
      const screenX1 = selectionArea.x1 * zoom + panOffset.x;
      const screenY1 = selectionArea.y1 * zoom + panOffset.y;
      const screenX2 = selectionArea.x2 * zoom + panOffset.x;
      const screenY2 = selectionArea.y2 * zoom + panOffset.y;

      ctx.save();
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
      ctx.setLineDash([6, 2]);
      ctx.strokeRect(screenX1, screenY1, screenX2 - screenX1, screenY2 - screenY1);
      ctx.fillRect(screenX1, screenY1, screenX2 - screenX1, screenY2 - screenY1);
      ctx.restore();
    }

    if (snapGuides.length > 0) {
      ctx.save();
      ctx.strokeStyle = "rgba(0, 255, 0, 0.8)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      snapGuides.forEach((guide) => {
        const position = guide.position * zoom + panOffset[guide.type === "vertical" ? "x" : "y"];
        const start = guide.start * zoom + panOffset[guide.type === "vertical" ? "y" : "x"];
        const end = guide.end * zoom + panOffset[guide.type === "vertical" ? "y" : "x"];

        ctx.beginPath();
        if (guide.type === "vertical") {
          ctx.moveTo(position, start);
          ctx.lineTo(position, end);
        } else {
          ctx.moveTo(start, position);
          ctx.lineTo(end, position);
        }
        ctx.stroke();
      });

      ctx.restore();
    }

    pendingGroups.forEach((group) => {
      // Draw group elements
      group.elements.forEach((element) => {
        const x1 = (element.x1 + (group.offset?.x || 0)) * zoom + panOffset.x;
        const y1 = (element.y1 + (group.offset?.y || 0)) * zoom + panOffset.y;
        const x2 = (element.x2 + (group.offset?.x || 0)) * zoom + panOffset.x;
        const y2 = (element.y2 + (group.offset?.y || 0)) * zoom + panOffset.y;

        // Draw bin with semi-transparent fill
        ctx.save();
        ctx.strokeStyle = "blue";
        ctx.fillStyle = "rgba(0, 0, 255, 0.1)";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

        // Draw bin code
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `${Math.min(20, Math.floor((x2 - x1) / 10))}px sans-serif`;
        ctx.fillStyle = "black";
        ctx.fillText(element.code, (x1 + x2) / 2, (y1 + y2) / 2);
        ctx.restore();
      });
    });
  };

  const animatingRef = useRef(false);
  useEffect(() => {
    if (animatingRef.current) {
      return;
    }

    animatingRef.current = true;
    const throttledFunc = throttleRAF(renderScene, { trailing: true });
    throttledFunc();

    return () => {
      animatingRef.current = false;
      throttledFunc.cancel();
    };
  });

  const setTool = (tool) => {
    dispatch({ type: "RE_SET_TOOL", payload: tool });
  };

  const resetView = () => {
    dispatch({ type: "RE_SET_ZOOM", payload: 1 });
    dispatch({ type: "RE_SET_PAN_OFFSET", payload: { x: 75, y: 440 } });
  };

  const scrollToElement = (element) => {
    const { panOffset, zoom } = state.rackEditor;
    // debugger;
    let elementCenter = {
      x: (element.x1 + element.x2) / 2,
      y: (element.y1 + element.y2) / 2,
    };

    const targetPanOffset = {
      x: -elementCenter.x * zoom + window.innerWidth / 2,
      y: -elementCenter.y * zoom + window.innerHeight / 2,
    };

    const cancelAnimation = easeToValuesRAF({
      fromValues: state.layoutEditor.panOffset,
      toValues: { x: targetPanOffset.x, y: targetPanOffset.y },
      duration: 1000,
      onStep: (values) => {
        dispatch({
          type: "RE_SET_PAN_OFFSET",
          payload: values,
        });
      },
    });
    setAnimations([...animations, { cancel: cancelAnimation }]);
  };
  const handleZoomAction = (currentZoom, zoomBy) => {
    const newZoom = zoom + zoomBy;
    if (newZoom < 0.1) return;
    if (newZoom > 5) return;
    const screenCenter = {
      x: canvasDimensions.width / 2,
      y: canvasDimensions.height / 2,
    };
    const mouseX = (screenCenter.x - panOffset.x) / zoom;
    const mouseY = (screenCenter.y - panOffset.y) / zoom;

    const panXOffset = screenCenter.x - mouseX * newZoom;
    const panYOffset = screenCenter.y - mouseY * newZoom;

    dispatch({ type: "RE_SET_PAN_OFFSET", payload: { x: panXOffset, y: panYOffset } });
    dispatch({ type: "RE_SET_ZOOM", payload: newZoom });
  };

  const onLabelSubmit = (values) => {
    const actionsToDispatch = [];

    const labelMode = values.labelType;
    const label = values.labelName;

    selectedElements.forEach((elementId) => {
      const element = elements.find((e) => e.id === elementId);
      const existingLabels = element.labels || [];
      const existingLabelWithType = existingLabels.find((l) => l.type === labelMode);
      if (existingLabelWithType) {
        existingLabelWithType.name = label;
      } else {
        existingLabels.push({ type: labelMode, name: label });
      }
      actionsToDispatch.push({
        type: "RE_UPDATE_ELEMENT",
        payload: { ...element, labels: existingLabels },
      });
    });
    dispatch({
      type: "BATCH_DISPATCH",
      payload: actionsToDispatch,
    });
    setLabelModal(false);
  };

  const handleBinSubmit = async (binData) => {
    const { rackId, id, code, objectType } = binData;
    try {
      const isExistingBin = !isNaN(+id); // If id is number it is a existingBin and it returns true otherwise false

      if (isExistingBin) {
        setBinEditLoading(true);

        const prevBinCode = state.rackEditor.selectedElement.code;

        // If the bin code has been changed, update the location in inventory in secondary database
        const { data } = await updateLayoutObject({ rackId, id, code, prevCode: prevBinCode, objectType });

        if (!data) return;
      }

      const updatedElementsList = elements.map((element) => (binData.id === element.id ? binData : element));
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "RE_SET_ELEMENTS", payload: updatedElementsList },
          { type: "RE_SET_SELECTED_ELEMENT", payload: binData },
        ],
      });
      setBinModal(false);

      if (isExistingBin) toast.success("Bin code updated successfully");
    } catch (error) {
      const {
        response: {
          data: { message },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      toast.error(message || defaultErrorMessage);
    } finally {
      setBinEditLoading(false);
    }
  };

  const onGenerateBinsSubmit = (payload) => {
    // If using Upload + Form.Item, get the first file from fileList
    const file = payload.excelFile || null;

    if (file) {
      generateBinsFromExcel(payload);
      setToggleGenerateBins(false);
      return;
    }

    generateBins(payload);
    setToggleGenerateBins(false);
  };

  const generateBinsFromExcel = async ({ excelFile, formValues, formData, selectedSheetName = null }) => {
    try {
      // Dynamically import XLSX from CDN
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-latest/package/xlsx.mjs");

      const data = await excelFile.arrayBuffer();
      const workbook = XLSX.read(data);

      // Get all sheet names
      const sheetNames = workbook.SheetNames;

      if (!sheetNames.length) {
        message.error("Excel file has no sheets!");
        return;
      }

      // Use selected sheet or default to first sheet
      const targetSheetName = selectedSheetName || sheetNames[0];
      const sheet = workbook.Sheets[targetSheetName];

      if (!sheet) {
        message.error(`Sheet "${targetSheetName}" not found in Excel file`);
        return;
      }

      const sampleRow = {
        "Bin Name": "Sample bin Name",
        "Rack Name": "Sample rack name",
        "Row No": 1,
        "Column No": 1,
        "Bay Label": "Bay A",
        "Column Label": "Column A",
        "Level Label": "Level A",
        "Bin Width": 100,
        "Bin Height": 100,
        "Unit Type (inch / feet)": "inch",
        __EMPTY: " ( Remove this row, before inserting your data. )",
      };

      const getExcelBinsRows = (sheet) => {
        const rawExcelRows = XLSX.utils.sheet_to_json(sheet);
        const isFirstRowSampleRow = JSON.stringify(rawExcelRows[0]) === JSON.stringify(sampleRow);

        if (isFirstRowSampleRow) {
          return rawExcelRows.slice(1);
        }

        return rawExcelRows;
      };

      // Read Excel into JSON
      const excelRows = getExcelBinsRows(sheet);

      if (!excelRows.length) {
        message.error("Excel is empty or columns missing!");
        return;
      }

      const { levels: enteredRows, columns: enteredColumns } = formValues;
      const { rackHeight, rackWidth, unitType: rackUnitType } = formData;

      // Column mapping configuration
      const COLUMN_MAP = {
        // Required fields
        BIN_NAME: "Bin Name",
        RACK_CODE: "Rack Code",
        COLUMN_NO: "Column No",
        ROW_NO: "Row No",
        BIN_WIDTH: "Bin Width",
        BIN_HEIGHT: "Bin Height",

        // Optional fields
        UNIT_TYPE: "Unit Type (inch / feet)",
        MAX_CAPACITY: "Max Capacity",

        // Label fields
        LEVEL_LABEL: "Level Label",
        COLUMN_LABEL: "Column Label",
        BAY_LABEL: "Bay Label",
      };

      // Helper function to get value from row with fallback
      const getValue = (row, key, defaultValue = null) => {
        return row[key] !== undefined ? row[key] : defaultValue;
      };

      const rackBins = [];
      // Check if Excel has Row No and Column No columns
      const firstRow = excelRows[0];
      const hasRowNo = COLUMN_MAP.ROW_NO in firstRow && firstRow[COLUMN_MAP.ROW_NO] !== undefined;
      const hasColumnNo = COLUMN_MAP.COLUMN_NO in firstRow && firstRow[COLUMN_MAP.COLUMN_NO] !== undefined;
      const hasBinWidth = COLUMN_MAP.BIN_WIDTH in firstRow && firstRow[COLUMN_MAP.BIN_WIDTH] !== undefined;
      const hasBinHeight = COLUMN_MAP.BIN_HEIGHT in firstRow && firstRow[COLUMN_MAP.BIN_HEIGHT] !== undefined;

      let maxRow = 0;
      let maxColumn = 0;
      let autoCalculatedWidth = null;
      let autoCalculatedHeight = null;

      // CASE 1: Excel has Row No and Column No columns
      if (hasRowNo && hasColumnNo) {
        // First pass: find maximum rows and columns to understand the grid size
        excelRows.forEach((excelRow) => {
          const row = Number(getValue(excelRow, COLUMN_MAP.ROW_NO));
          const column = Number(getValue(excelRow, COLUMN_MAP.COLUMN_NO));
          if (row > maxRow) maxRow = row;
          if (column > maxColumn) maxColumn = column;
        });
      }
      // CASE 2: Excel doesn't have Row No and Column No columns
      else {
        // Validate that enteredRows and enteredColumns are provided
        if (!enteredRows || !enteredColumns) {
          message.error("Excel doesn't contain Row No and Column No columns. Please enter Rows and Columns in the form.");
          return;
        }

        maxRow = parseInt(enteredRows, 10);
        maxColumn = parseInt(enteredColumns, 10);

        if (maxRow <= 0 || maxColumn <= 0) {
          message.error("Rows and Columns must be positive numbers.");
          return;
        }

        // Calculate how many bins we need based on Excel rows
        const totalBinsNeeded = maxRow * maxColumn;
        const actualBins = excelRows.length;

        // Handle different scenarios
        if (actualBins === totalBinsNeeded) {
          // Perfect match - no adjustment needed
        } else if (actualBins > totalBinsNeeded) {
          // More bins than grid can hold - show warning but proceed with available grid
          message.warning(
            `Excel has ${actualBins} bins, but grid can only hold ${totalBinsNeeded} (${enteredRows} rows × ${enteredColumns} columns). Only first ${totalBinsNeeded} bins will be used.`,
          );

          // Truncate the excelRows to fit the grid
          excelRows.splice(totalBinsNeeded);
        } else if (actualBins < totalBinsNeeded) {
          // Fewer bins than grid - adjust rows dynamically
          const calculatedRows = Math.ceil(actualBins / maxColumn);
          const usedBins = Math.min(actualBins, calculatedRows * maxColumn);
          // message.info(`Excel has ${actualBins} bins. Adjusting to ${calculatedRows} rows × ${maxColumn} columns to fit all bins.`);
          message.info(
            `Excel has ${actualBins} bins. Adjusting layout to ${calculatedRows} rows × ${maxColumn} columns. ` +
              `This will use ${usedBins} bins with ${calculatedRows * maxColumn - actualBins} empty slots.`,
          );

          maxRow = calculatedRows;
        }
      }

      // Auto-calculate bin dimensions if rack dimensions are available AND bin dimensions are not provided in Excel
      if (rackWidth && rackHeight && maxColumn > 0 && maxRow > 0) {
        const rackWidthPx = unitsToPixels(Number(rackWidth), rackUnitType);
        const rackHeightPx = unitsToPixels(Number(rackHeight), rackUnitType);

        // Calculate optimal bin dimensions
        if (!hasBinWidth || !hasBinHeight) {
          // Calculate bin dimensions to fit the rack perfectly
          const binWidthFromRack = rackWidthPx / maxColumn;
          const binHeightFromRack = rackHeightPx / maxRow;

          // Make bins square by using the smaller dimension to maintain aspect ratio
          const optimalBinSize = Math.min(binWidthFromRack, binHeightFromRack);

          autoCalculatedWidth = round(optimalBinSize);
          autoCalculatedHeight = round(optimalBinSize);

          // Calculate how many bins will actually fit
          const binsThatFitWidth = Math.floor(rackWidthPx / optimalBinSize);
          const binsThatFitHeight = Math.floor(rackHeightPx / optimalBinSize);
          const totalBinsThatFit = binsThatFitWidth * binsThatFitHeight;

          if (excelRows.length > totalBinsThatFit) {
            message.warning(
              `Rack can only fit ${totalBinsThatFit} bins (${binsThatFitHeight} rows × ${binsThatFitWidth} columns) ` +
                `but Excel has ${excelRows.length} bins. Some bins may be positioned outside rack boundaries.`,
            );
          }
        } else {
          // Use rack dimensions for validation only when Excel provides bin dimensions
          autoCalculatedWidth = round(rackWidthPx / maxColumn);
          autoCalculatedHeight = round(rackHeightPx / maxRow);
        }
      }

      excelRows.forEach((excelRow, index) => {
        // Map Excel columns to expected keys
        const binName = getValue(excelRow, COLUMN_MAP.BIN_NAME);
        const binWidth = getValue(excelRow, COLUMN_MAP.BIN_WIDTH);
        const binHeight = getValue(excelRow, COLUMN_MAP.BIN_HEIGHT);
        const unitType = getValue(excelRow, COLUMN_MAP.UNIT_TYPE, "feet");
        const maxCapacity = getValue(excelRow, COLUMN_MAP.MAX_CAPACITY);

        // Labels from Excel
        const levelLabel = getValue(excelRow, COLUMN_MAP.LEVEL_LABEL);
        const columnLabel = getValue(excelRow, COLUMN_MAP.COLUMN_LABEL);
        const bayLabel = getValue(excelRow, COLUMN_MAP.BAY_LABEL);

        // Calculate row and column numbers
        let rowNo, column;

        if (hasRowNo && hasColumnNo) {
          // Use values from Excel
          rowNo = getValue(excelRow, COLUMN_MAP.ROW_NO);
          column = getValue(excelRow, COLUMN_MAP.COLUMN_NO);
        } else {
          // Auto-assign row and column based on index
          // Example: 100 bins, 10 rows × 10 columns
          // index 0 → row 1, column 1
          // index 1 → row 1, column 2
          // ...
          // index 9 → row 1, column 10
          // index 10 → row 2, column 1
          rowNo = Math.floor(index / maxColumn) + 1;
          column = (index % maxColumn) + 1;
        }

        // Validate required fields
        const missingFields = [];
        if (!binName) missingFields.push(COLUMN_MAP.BIN_NAME);
        if (column === undefined || column === null) missingFields.push(COLUMN_MAP.COLUMN_NO);
        if (rowNo === undefined || rowNo === null) missingFields.push(COLUMN_MAP.ROW_NO);
        // if (!binWidth) missingFields.push(COLUMN_MAP.BIN_WIDTH);
        // if (!binHeight) missingFields.push(COLUMN_MAP.BIN_HEIGHT);
        if (!hasBinWidth && !autoCalculatedWidth) missingFields.push(COLUMN_MAP.BIN_WIDTH);
        if (!hasBinHeight && !autoCalculatedHeight) missingFields.push(COLUMN_MAP.BIN_HEIGHT);

        if (missingFields.length) {
          message.error(`Row ${index + 1}: Missing required fields - ${missingFields.join(", ")}`);
          return;
        }

        // Calculate bin dimensions
        let finalWidth, finalHeight;

        if (binWidth && binHeight) {
          // Use dimensions from Excel
          finalWidth = unitsToPixels(Number(binWidth), unitType.toLowerCase());
          finalHeight = unitsToPixels(Number(binHeight), unitType.toLowerCase());
        } else if (autoCalculatedWidth && autoCalculatedHeight) {
          // Use auto-calculated dimensions
          finalWidth = autoCalculatedWidth;
          finalHeight = autoCalculatedHeight;
        } else {
          message.error(`Row ${index + 1}: No bin dimensions provided and cannot auto-calculate`);
          return;
        }

        const formattedWidth = round(finalWidth);
        const formattedHeight = round(finalHeight);

        // Calculate positions - EXACTLY matching manual generation pattern
        // Convert to zero-based indexing for internal calculations
        const internalRow = Number(rowNo) - 1;
        const internalColumn = Number(column) - 1;

        // Calculate positions - using row and column for 2D grid
        // Assuming rows go from left to right, levels go from bottom to top
        const xPos = internalColumn * formattedWidth;
        // For level 1: (0 * height + height) * -1 = -height (bottom)
        // For level 2: (1 * height + height) * -1 = -2*height (above level 1)
        const yPos = (internalRow * formattedHeight + formattedHeight) * -1;

        // Build labels array dynamically
        const labels = [];

        if (levelLabel) {
          labels.push({
            type: "level",
            name: String(levelLabel),
          });
        }

        if (columnLabel) {
          labels.push({
            type: "column",
            name: String(columnLabel),
          });
        }

        if (bayLabel) {
          labels.push({
            type: "bay",
            name: String(bayLabel),
          });
        }

        // If we want to add custom labels
        // Example for additional custom labels:
        // if (row["Custom Label"]) {
        //     labels.push({
        //         type: "custom",
        //         name: String(row["Custom Label"])
        //     });
        // }

        // For 3D visualization, you might want to use:
        // x: column, y: level, z: row (if you have depth)
        const bin = {
          name: binName,
          code: binName,
          id: `Bin${internalRow}-${internalColumn}`, // Match manual ID format: Bin0-0, Bin0-1, etc.
          type: "rectangle",
          objectType: "bin",
          width: formattedWidth,
          height: formattedHeight,
          x1: xPos,
          y1: yPos,
          x2: xPos + formattedWidth,
          y2: yPos + formattedHeight,
          color: "black",
          labels: labels,
          maxCapacity: maxCapacity !== undefined ? parseInt(maxCapacity, 10) : null,
          // Store additional info
          // levelNo: Number(level),
          columnNo: Number(column),
          unitType: unitType,
        };

        // rackGroup.bins.push(bin);
        rackBins.push(bin);
      });

      // Sort bins by level and column to maintain consistent order
      rackBins.sort((a, b) => {
        if (a.levelNo !== b.levelNo) return a.levelNo - b.levelNo;
        return a.columnNo - b.columnNo;
      });

      if (!rackBins.length) {
        message.error("Could not generate bins. Please check data.");
        return;
      }

      // Update UI
      dispatch({ type: "RE_SET_ELEMENTS", payload: rackBins });
      message.success(`✅ Successfully created ${rackBins.length} bins from Excel`);
    } catch (err) {
      console.error("Excel Parsing Error:", err);
      message.error("Failed to read Excel file. Please verify the format.");
    }
  };

  const generateBins = (payload) => {
    const { maxCapacity } = payload;
    let { levels, columns, binWidth, binHeight } = payload;

    binWidth = unitsToPixels(binWidth, formData.unitType);
    binHeight = unitsToPixels(binHeight, formData.unitType);
    binWidth = round(binWidth, 2);
    binHeight = round(binHeight, 2);

    const rackBins = [];

    for (let level = 0; level < levels; level++) {
      for (let column = 0; column < columns; column++) {
        const bin = {
          name: `${formData.rackCode}_Bin_${level * columns + column}`,
          code: `${formData.rackCode}_Bin_${level * columns + column}`,
          id: `Bin${level}-${column}`,
          type: "rectangle",
          objectType: "bin",
          width: binWidth,
          height: binHeight,
          x1: column * binWidth,
          y1: (level * binHeight + binHeight) * -1,
          x2: column * binWidth + binWidth,
          y2: level * binHeight * -1,
          color: "black",
          labels: [],
          maxCapacity,
        };
        rackBins.push(bin);
      }
    }

    dispatch({ type: "RE_SET_ELEMENTS", payload: rackBins });
  };

  //toggle FullScreen
  const toggleFullScreen = () => {
    const layoutFullscreen = document.querySelector(".rack-editor-canvas-container");
    if (state.FULLSCREEN_MODE_ENABLED) {
      document.exitFullscreen();
    } else {
      layoutFullscreen.requestFullscreen();
    }
  };

  //auot generate bins modal popup
  const [isModalOpen, setIsModalOpen] = useState(false);
  const autoGenerateBins = () => {
    setIsModalOpen(true);
  };

  const [binCloningInfo, setBinCloningInfo] = useState({
    direction: "",
    count: 0,
  });

  const [isCollisionModalVisible, setIsCollisionModalVisible] = useState(false);
  const [collisionBinNumber, setCollisionBinNumber] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState("");

  const showCollisionPopUp = (binNumber) => {
    setCollisionBinNumber(binNumber);
    setIsCollisionModalVisible(true);
  };
  const handleDirectionClick = (direction) => {
    setSelectedDirection(direction);
  };

  const updateCloningCount = (count) => {
    setBinCloningInfo({
      ...binCloningInfo,
      count,
    });
  };
  const handleOk = () => {
    if (selectedDirection) {
      const count = binCloningInfo.count;
      const selectedBin = selectedElement;
      const bins = [...elements];

      const calculateNewBinPosition = (index, selectedDirection) => {
        const binCodeParts = selectedBin.code.split("_");
        const selectedBinNumber = binCodeParts.length > 1 ? parseInt(binCodeParts[1]) : 0;
        const baseConfig = {
          ...selectedBin,
          code: `Bin_${selectedBinNumber + index}`,
          id: `bin_${Date.now()}_${index}`,
        };

        switch (selectedDirection) {
          case "LTR":
            return {
              ...baseConfig,
              x1: selectedBin.x1 + index * selectedBin.width,
              x2: selectedBin.x1 + index * selectedBin.width + selectedBin.width,
            };
          case "RTL":
            return {
              ...baseConfig,
              x1: selectedBin.x1 - index * selectedBin.width,
              x2: selectedBin.x1 - index * selectedBin.width + selectedBin.width,
            };
          case "TTB":
            return {
              ...baseConfig,
              y1: selectedBin.y1 + index * selectedBin.height,
              y2: selectedBin.y1 + index * selectedBin.height + selectedBin.height,
            };
          case "BTT":
            return {
              ...baseConfig,
              y1: selectedBin.y1 - index * selectedBin.height,
              y2: selectedBin.y1 - index * selectedBin.height + selectedBin.height,
            };
        }
      };

      for (let i = 1; i <= count; i++) {
        const newBin = calculateNewBinPosition(i, selectedDirection);
        const collisionOfBins = elements.some((element) => element.id !== newBin.id && isColliding(element, newBin));

        if (collisionOfBins) {
          showCollisionPopUp(i);
          toast.warning("Cannot place Bin due to collision with existing bins." || defaultErrorMessage);
          break;
        }
        bins.push(newBin);
      }

      dispatch({ type: "RE_SET_ELEMENTS", payload: bins });
      updateCloningCount(0);
      setIsModalOpen(false);
    } else {
      console.log("Please select a direction first");
    }
  };

  const handleCancel = () => {
    updateCloningCount(0);
    setIsModalOpen(false);
  };

  const menu = (toggleLabelModal) => {
    const onHandleLabelType = (e) => {
      const labelType = e.key === "1" ? "BARCODE" : "QRCODE";
      setScannableLabelType(labelType);
      // setBarCodeModal(true);
      toggleLabelModal(true);
    };
    return (
      <Menu>
        <Menu.Item key="1" onClick={onHandleLabelType}>
          BarCode
        </Menu.Item>
        <Menu.Item key="2" onClick={onHandleLabelType}>
          QR Code
        </Menu.Item>
      </Menu>
    );
  };

  const onMultiBinEditSubmit = async (data) => {
    const { maxCapacity } = data;
    
    // Create batch update actions
    const updateActions = Array.from(selectedElements).map(elementId => {
      const element = elements.find(el => el.id === elementId);
      if (!element) return null;
      
      return {
        type: "RE_UPDATE_ELEMENT",
        payload: {
          ...element,                       
          maxCapacity: maxCapacity
        }
      };
    }).filter(Boolean);

    if (updateActions.length > 0) {
      console.log(selectedRackInfo);
      const rackId = selectedRackInfo.id;
      
      const binIds = Array.from(selectedElements);
      await axios.put(`/locations/rack/${rackId}/locations/bulk-update`, { binIds, maxCapacity });
      dispatch({
        type: "BATCH_DISPATCH",
        payload: updateActions
      });
      message.success(`Updated ${updateActions.length} bins`);
    }

    setShowMultiBinEditModal(false);
  };

  const onMultiBinEditCancel = () => {
    setShowMultiBinEditModal(false);
  };

  const buttonWidth = 20;
  return (
    <div className="rack-editor-container" ref={containerRef}>
      <div className="rack-editor-canvas-container" ref={canvasParentRef}>
        <div className="canvas-actions">
          <div className="action-group">
            <Tooltip title={"Select"}>
              <div className={`action select ${tool === "selection" ? "active" : ""} cursor-pointer`} onClick={() => setTool("selection")}>
                <FontAwesomeIcon icon={faMousePointer} className="canvas-control-icon" />
              </div>
            </Tooltip>
            <Tooltip title={"Pan"}>
              <div className={`action pan ${tool === "pan" ? "active" : ""} cursor-pointer`} onClick={() => setTool("pan")}>
                <FontAwesomeIcon icon={faHand} className="canvas-control-icon" />
              </div>
            </Tooltip>
            <Tooltip title={"Draw"}>
              <div className={`action draw ${tool === "draw" ? "active" : ""} cursor-pointer`} onClick={() => setTool("draw")}>
                <FontAwesomeIcon icon={faDrawPolygon} className="canvas-control-icon" />
              </div>
            </Tooltip>
            <Tooltip title={"Re-center"}>
              <div className={`action reset cursor-pointer`} onClick={resetView}>
                <FontAwesomeIcon icon={faArrowsToCircle} className="canvas-control-icon" />
              </div>
            </Tooltip>
            <Tooltip title={"Generate Bins"}>
              <div className={`action generateBins cursor-pointer`} onClick={() => setToggleGenerateBins(true)}>
                <FontAwesomeIcon icon={faGrip} className="canvas-control-icon" />
              </div>
            </Tooltip>

            <Dropdown overlay={menu(setToggleExportBarCodes)} trigger={["click"]}>
              <div className={`action generateBins cursor-pointer ${tool === "print" ? "active" : ""}`} onClick={() => setTool("print ")}>
                {" "}
                <FontAwesomeIcon icon={faPrint} className="canvas-control-icon" />
              </div>
            </Dropdown>
          </div>

          {selectedElements.size > 0 && (
            <div className="action-group extended-actions">
              <Tooltip title={"Add Label"}>
                <div className={`action labels cursor-pointer ${tool === "Label" ? "active" : ""}`} onClick={handleLabels}>
                  <FontAwesomeIcon icon={faTags} className="canvas-control-icon" />
                </div>
              </Tooltip>

              {selectedElements.size === 1 && (
                <>
                  <Tooltip title={"Edit"}>
                    <div className={`action edit cursor-pointer ${tool === "BinInfo" ? "active" : ""}`} onClick={handleBinInfo}>
                      <FontAwesomeIcon icon={faEdit} className="canvas-control-icon" />
                    </div>
                  </Tooltip>
                  <Dropdown overlay={menu(setBarCodeModal)} trigger={["hover"]}>
                    <div className={`action reset cursor-pointer ${tool === "barcode" ? "active" : ""}`} onClick={() => setTool("barcode")}>
                      {" "}
                      <FontAwesomeIcon icon={faBarcode} className="canvas-control-icon" />
                    </div>
                  </Dropdown>
                </>
              )}

              {selectedElements.size > 1 && (
                <>
                <Tooltip title={"Generate Bin Labels"}>
                  <div className={`action reset cursor-pointer`} onClick={() => setShowGenerateBinLabelsForm(true)}>
                    <FontAwesomeIcon icon={faBox} className="canvas-control-icon" />
                  </div>
                </Tooltip>
                {/* Edit Bin Details */}
                <Tooltip title={"Edit Bin Details"}>
                  <div className={`action reset cursor-pointer`} onClick={() => setShowMultiBinEditModal(true)}>
                    <FontAwesomeIcon icon={faEdit} className="canvas-control-icon" />
                  </div>
                </Tooltip>
                </>
              )}
              <Tooltip title={"Generate Bins"}>
                <div className={`action`} onClick={() => autoGenerateBins()}>
                  <FontAwesomeIcon icon={faCube} className="canvas-control-icon" />
                </div>
              </Tooltip>
              <Modal
                title="Clone Bins"
                open={isModalOpen}
                width={"400px"}
                centered
                onOk={handleOk}
                onCancel={handleCancel}
                disabled={!selectedDirection}
                okButtonProps={{ disabled: !binCloningInfo.count || binCloningInfo.count === 0 }}
              >
                <div className="label-container" style={{ marginTop: "15px" }}>
                  <Row>
                    <Col span={12} style={{ marginTop: "15px" }}>
                      <label style={{ fontWeight: "500" }}>Bin Count</label>
                      <Input type="number" value={binCloningInfo.count} onChange={(e) => updateCloningCount(e.target.value)} />
                    </Col>
                    <Col span={12}>
                      <ConfigProvider
                        button={{
                          style: {
                            width: buttonWidth,
                            margin: 4,
                          },
                        }}
                      >
                        <Flex vertical justify="center" align="center" className="demo">
                          <Flex
                            justify="center"
                            align="center"
                            style={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Tooltip placement="top">
                              <Button
                                className={`direction-label-btn ${selectedDirection === "BTT" ? "active" : ""}`}
                                onClick={() => handleDirectionClick("BTT")}
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </Button>
                            </Tooltip>
                          </Flex>
                          <Flex
                            style={{
                              width: buttonWidth * 5 + 32,
                            }}
                            justify="space-between"
                            align="center"
                          >
                            <Flex align="center" vertical>
                              <Tooltip placement="left">
                                <Button
                                  className={`direction-label-btn ${selectedDirection === "RTL" ? "active" : ""}`}
                                  onClick={() => handleDirectionClick("RTL")}
                                >
                                  <FontAwesomeIcon icon={faArrowLeft} />
                                </Button>
                              </Tooltip>
                            </Flex>
                            <Flex align="center" vertical>
                              <Tooltip placement="right">
                                <Button
                                  className={`direction-label-btn ${selectedDirection === "LTR" ? "active" : ""}`}
                                  onClick={() => handleDirectionClick("LTR")}
                                >
                                  <FontAwesomeIcon icon={faArrowRight} />
                                </Button>
                              </Tooltip>
                            </Flex>
                          </Flex>
                          <Flex
                            justify="center"
                            align="center"
                            style={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Tooltip placement="bottom">
                              <Button
                                className={`direction-label-btn ${selectedDirection === "TTB" ? "active" : ""}`}
                                onClick={() => handleDirectionClick("TTB")}
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </Button>
                            </Tooltip>
                          </Flex>
                        </Flex>
                      </ConfigProvider>
                    </Col>
                  </Row>
                </div>
              </Modal>
              <Tooltip title={"Delete"}>
                <div className={`action delete ${selectedElements.size ? "active" : "disabled"}`} onClick={handleDelete}>
                  <FontAwesomeIcon icon={faTrash} className="canvas-control-icon" />
                </div>
              </Tooltip>
            </div>
          )}
        </div>

        <canvas
          id="canvas"
          ref={canvasRef}
          width={canvasDimensions.width}
          height={canvasDimensions.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
        {pendingGroups.map((group, index) => {
          const bounds = getGroupBounds(group);
          const relativeBounds = getRelativeBounds(bounds);
          return (
            <GroupControls
              key={group.id || index}
              group={group}
              bounds={relativeBounds}
              zoom={zoom}
              panOffset={panOffset}
              onFinalize={() => finalizeGroup(index)}
              onCancel={() => cancelGroup(index)}
            />
          );
        })}
        <ToolTip title={"Fullscreen"}>
          <button className="toggleFullScreen" type="button" onClick={toggleFullScreen}>
            <FontAwesomeIcon icon={state.FULLSCREEN_MODE_ENABLED ? faCompress : faExpand} />
          </button>
        </ToolTip>
        <div className="zoom-action-wrapper">
          <ToolTip title={"Zoom"}>
            <div className="zoom-action-main">
              <button className="zoom-action-btn" type="button" onClick={() => handleZoomAction(zoom, -0.1)} disabled={zoom <= 0.1}>
                -
              </button>
              <div className="zoom-action-stat">{round(zoom * 100)}%</div>
              <button className="zoom-action-btn" type="button" onClick={() => handleZoomAction(zoom, 0.1)} disabled={zoom >= 2}>
                +
              </button>
            </div>
          </ToolTip>
        </div>
      </div>
      {labelModal && <BinLabelsForm onSubmit={onLabelSubmit} setLabelModal={setLabelModal} />}

      {binModal && (
        <BinEditForm
          selectedElementId={[...selectedElements][0]}
          elements={elements}
          onSubmit={handleBinSubmit}
          setBinModalVisible={setBinModal}
          formData={formData}
          loading={binEditLoading}
        />
      )}
      {showGenerateBinLabelsForm && selectedElements.size > 1 && (
        <GenerateBinLabelsForm
          showGenerateBinLabelsForm={showGenerateBinLabelsForm}
          selectedElements={selectedElements}
          setShowGenerateBinLabelsForm={setShowGenerateBinLabelsForm}
        />
      )}
      {barCodeModal && (
        <Modal
          style={{ margin: "25px 0px" }}
          centered
          maskClosable={false}
          width={350}
          cancelText={"Cancel"}
          okText={"Save"}
          title={"Label Preview"}
          open={barCodeModal}
          onCancel={() => setBarCodeModal(false)}
          footer={null}
        >
          {scannableLabelType === "BARCODE" ? (
            <BarCode text={selectedElement.code} showActions={true} />
          ) : (
            <QrCode value={selectedElement.code} showActions={true} />
          )}
        </Modal>
      )}

      {toggleGenerateBins && (
        <GenerateBinsModal onSubmit={onGenerateBinsSubmit} setToggleGenerateBins={setToggleGenerateBins} formData={formData} bins={selectedElements} />
      )}

      {toggleExportBarCodes && (
        <ExportBarCodesModal elements={elements} formData={formData} setToggleExportBarCodes={setToggleExportBarCodes} labelType={scannableLabelType} />
      )}
      {showMultiBinEditModal && selectedElements.size > 1 && (
        <MultiBinEdit onSubmit={onMultiBinEditSubmit} onCancel={onMultiBinEditCancel} elements={selectedElements} />
      )}
    </div>
  );
};

export default RackFrontView;
